import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  ChevronLeft, ChevronRight, Layers, Users, Calendar, Trophy,
  MapPin, Clock, Filter, Sparkles,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEVEL_STYLES = {
  Gold: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-700' },
  Silver: { bg: 'bg-zinc-400/10', text: 'text-zinc-300', border: 'border-zinc-600' },
  Bronze: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-700' },
};

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const ProgramTeams = () => {
  const { slug } = useParams();
  const [program, setProgram] = useState(null);
  const [teams, setTeams] = useState([]);
  const [nextGames, setNextGames] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterAge, setFilterAge] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [filterDivision, setFilterDivision] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const progRes = await fetch(`${API_URL}/api/programs/by-slug/${slug}`);
        if (!progRes.ok) { setLoading(false); return; }
        const prog = await progRes.json();
        setProgram(prog);

        const teamsRes = await fetch(`${API_URL}/api/programs/${prog.id}/teams`);
        if (teamsRes.ok) {
          const teamsData = await teamsRes.json();
          setTeams(teamsData);

          // Fetch next game for each team
          const gameMap = {};
          const schedRes = await fetch(`${API_URL}/api/programs/${prog.id}/schedule?limit=50`);
          if (schedRes.ok) {
            const events = await schedRes.json();
            const now = new Date().toISOString();
            for (const t of teamsData) {
              const nextGame = events.find(e =>
                e.team_id === t.id &&
                ['game', 'tournament'].includes(e.event_type) &&
                e.start_time >= now
              );
              if (nextGame) gameMap[t.id] = nextGame;
            }
          }
          setNextGames(gameMap);
        }
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <p className="text-zinc-500">Program not found</p>
      </div>
    );
  }

  const color = program.primary_color || '#06b6d4';
  const colorBg = `${color}15`;

  // Derive unique filter options
  const ageGroups = [...new Set(teams.map(t => t.age_group).filter(Boolean))].sort();
  const levels = [...new Set(teams.map(t => t.level).filter(Boolean))].sort();
  const divisions = [...new Set(teams.map(t => t.division).filter(Boolean))].sort();

  const filtered = teams.filter(t =>
    (!filterAge || t.age_group === filterAge) &&
    (!filterLevel || t.level === filterLevel) &&
    (!filterDivision || t.division === filterDivision)
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <div className="border-b border-zinc-800/60" style={{ borderBottomColor: `${color}30` }}>
        <div className="max-w-6xl mx-auto px-4 py-5">
          <div className="flex items-center gap-2 mb-4">
            <Link to={`/${slug}/dashboard`} className="text-zinc-500 hover:text-zinc-300 text-xs flex items-center gap-1 transition-colors" data-testid="back-to-dashboard">
              <ChevronLeft className="w-3 h-3" /> Dashboard
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 rounded-xl shrink-0">
              <AvatarFallback className="rounded-xl text-sm font-black text-white" style={{ backgroundColor: color }}>
                {program.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white" data-testid="teams-page-title">{program.name}</h1>
              <p className="text-zinc-500 text-xs flex items-center gap-1">
                <Layers className="w-3 h-3" /> {teams.length} Teams
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Filters */}
        {(ageGroups.length > 1 || levels.length > 1 || divisions.length > 1) && (
          <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="team-filters">
            <Filter className="w-4 h-4 text-zinc-600" />
            {ageGroups.length > 1 && (
              <select
                value={filterAge}
                onChange={e => setFilterAge(e.target.value)}
                className="h-8 px-2 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-400 text-xs"
                data-testid="filter-age-group"
              >
                <option value="">All Ages</option>
                {ageGroups.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            )}
            {levels.length > 1 && (
              <select
                value={filterLevel}
                onChange={e => setFilterLevel(e.target.value)}
                className="h-8 px-2 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-400 text-xs"
                data-testid="filter-level"
              >
                <option value="">All Levels</option>
                {levels.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            )}
            {divisions.length > 1 && (
              <select
                value={filterDivision}
                onChange={e => setFilterDivision(e.target.value)}
                className="h-8 px-2 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-400 text-xs"
                data-testid="filter-division"
              >
                <option value="">All Divisions</option>
                {divisions.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            )}
            {(filterAge || filterLevel || filterDivision) && (
              <button
                onClick={() => { setFilterAge(''); setFilterLevel(''); setFilterDivision(''); }}
                className="text-[10px] text-zinc-500 hover:text-zinc-300 underline"
              >
                Clear
              </button>
            )}
          </div>
        )}

        {/* Team Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="teams-grid">
          {filtered.map(team => {
            const levelStyle = LEVEL_STYLES[team.level] || LEVEL_STYLES.Silver;
            const nextGame = nextGames[team.id];
            const initials = team.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

            return (
              <Card
                key={team.id}
                className="bg-zinc-900/80 border-zinc-800/60 hover:border-zinc-700 transition-all group overflow-hidden"
                data-testid={`team-card-${team.slug || team.id}`}
              >
                {/* Accent */}
                <div className="h-0.5" style={{ backgroundColor: color }} />
                <CardContent className="p-4">
                  {/* Header */}
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-sm font-bold text-white shrink-0" style={{ backgroundColor: `${color}25` }}>
                      {initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-bold text-base truncate group-hover:text-cyan-300 transition-colors">{team.name}</h3>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1">
                        {team.age_group && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-zinc-700 text-zinc-400">
                            {team.age_group}
                          </Badge>
                        )}
                        {team.level && (
                          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${levelStyle.text} ${levelStyle.border} ${levelStyle.bg}`}>
                            <Sparkles className="w-2.5 h-2.5 mr-0.5" />{team.level}
                          </Badge>
                        )}
                        {team.division && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-zinc-700 text-zinc-500">
                            {team.division}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Record */}
                  {team.record && team.record !== '0-0' && (
                    <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-zinc-800/50">
                      <Trophy className="w-4 h-4" style={{ color }} />
                      <span className="text-white font-bold font-mono text-sm">{team.record}</span>
                      <span className="text-zinc-500 text-[10px]">{team.season}</span>
                      <div className="ml-auto flex items-center gap-1 text-[10px] text-zinc-500">
                        <Users className="w-3 h-3" /> {team.member_count || 0}
                      </div>
                    </div>
                  )}

                  {/* Next Game */}
                  {nextGame ? (
                    <div className="mb-3 px-3 py-2 rounded-lg border border-zinc-800/60 bg-zinc-800/30">
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Next Game</div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5 text-zinc-500" />
                        <span className="text-white text-xs font-medium">{formatDate(nextGame.start_time)}</span>
                        {nextGame.opponent && (
                          <span className="text-xs text-zinc-400">vs <span className="text-white font-medium">{nextGame.opponent}</span></span>
                        )}
                      </div>
                      {nextGame.location && (
                        <div className="flex items-center gap-1.5 mt-1 text-[10px] text-zinc-500">
                          <MapPin className="w-3 h-3" /> {nextGame.location}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="mb-3 px-3 py-2 rounded-lg border border-dashed border-zinc-800 text-center">
                      <span className="text-[10px] text-zinc-600">No upcoming games</span>
                    </div>
                  )}

                  {/* CTA */}
                  <Link to={`/${slug}/teams/${team.slug || team.id}`} data-testid={`view-team-${team.slug || team.id}`}>
                    <Button className="w-full text-white text-xs font-semibold group-hover:opacity-100 opacity-90 transition-opacity" style={{ backgroundColor: color }}>
                      View Team <ChevronRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16">
            <Layers className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500">No teams match your filters</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgramTeams;
